import { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, PenLine, Type, Upload, ShieldCheck, Trash2, RefreshCcw } from "lucide-react";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Btn } from "./UI";
import { FLabel, FInput } from "../pages/engagement/FormControls";
import { SignaturePad } from "./SignaturePad";

/**
 * SignaturePromptModal - single global prompt used everywhere a user signs a record.
 *
 * Behavior contract:
 *   1. If the user has already saved a signature to their profile (Settings > E-Signature),
 *      show a preview and a big "Confirm and Sign" button.
 *   2. If they have not, show three tabs: Draw, Type, or Upload. When Upload is used, the
 *      image is passed through a client-side background-removal pass (near-white pixels
 *      become transparent) so the signature reads cleanly on any background.
 *   3. Every applied signature is submitted to the caller, which forwards it to the backend
 *      where a signature_id, doc_hash, IP, and signed_at are captured for audit. Optionally
 *      the user can tick "Save to my profile" so subsequent signs reuse the same image.
 *   4. First-use ESIGN Act consent: the user must acknowledge that their electronic
 *      signature has the same legal weight as a wet-ink signature before the first sign
 *      of a session. Consent is persisted in localStorage keyed by user id (or "portal")
 *      so subsequent signings in the same browser are one-step.
 *
 * Props:
 *   open, onClose
 *   onConfirm({ image, name, method, witness_name })  - fires when the user completes a sign flow
 *   title, subtitle                     - optional overrides
 *   busy                                - optional external busy flag to disable Confirm
 *   withWitness                         - when true, adds an optional witness name input
 *   signerName                          - optional override for signer name (portal use)
 *   allowSaveToProfile                  - when false, hides the Save to Profile checkbox
 */
export function SignaturePromptModal({ open, onClose, onConfirm, title = "Apply E-Signature", subtitle = "Your signature will be applied and locked with a HIPAA-compliant audit stamp.", busy: extBusy = false, withWitness = false, signerName: signerNameOverride, allowSaveToProfile = true }) {
  const auth = useAuth() || {};
  const user = auth.user || null;
  const refresh = auth.refresh;
  const [tab, setTab] = useState("draw");
  const [drawn, setDrawn] = useState("");
  const [typed, setTyped] = useState("");
  const [uploaded, setUploaded] = useState("");
  const [witness, setWitness] = useState("");
  const [saveToProfile, setSaveToProfile] = useState(false);
  const [busy, setBusy] = useState(false);
  const [esignConsented, setEsignConsented] = useState(false);

  const savedImage = user?.signature_image || "";
  const derivedSaved = user?.signature_name || `${user?.first_name || ""} ${user?.last_name || ""}`.trim();
  const savedName = signerNameOverride || derivedSaved;
  const savedCreds = user?.signature_credentials || "";
  const displayName = savedCreds && !signerNameOverride ? `${savedName}, ${savedCreds}` : savedName;
  const canOfferSaveToProfile = allowSaveToProfile && !!user?.id;
  const esignKey = user?.id ? `cocoa_esign_consent_${user.id}` : "cocoa_esign_consent_portal";
  // Annual re-attestation: if the saved signature is older than 365 days, we surface a
  // banner nudging the user to draw or type a fresh one. Non-blocking so they can still
  // continue in the Saved Signature tab if they choose.
  const savedAt = user?.signature_saved_at || "";
  let signatureAgeDays = null;
  if (savedAt) {
    try {
      const savedMs = new Date(savedAt).getTime();
      if (!Number.isNaN(savedMs)) {
        signatureAgeDays = Math.floor((Date.now() - savedMs) / 86400000);
      }
    } catch { signatureAgeDays = null; }
  }
  const reattestNeeded = signatureAgeDays !== null && signatureAgeDays >= 365;

  useEffect(() => {
    if (open) {
      setTab(savedImage ? "saved" : "draw");
      setDrawn(""); setTyped(""); setUploaded(""); setWitness("");
      setSaveToProfile(false);
      try {
        setEsignConsented(localStorage.getItem(esignKey) === "1");
      } catch { setEsignConsented(false); }
    }
  }, [open, savedImage, esignKey]);

  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open, onClose]);

  if (!open) return null;

  const runBackgroundRemoval = (dataUrl) => new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      // Downscale huge photos to keep the pixel walk under ~1 million pixels;
      // this avoids blocking the main thread when a user uploads a 4000x3000 phone photo.
      const MAX_DIM = 1200;
      const scale = Math.min(1, MAX_DIM / Math.max(img.width, img.height));
      const w = Math.max(1, Math.round(img.width * scale));
      const h = Math.max(1, Math.round(img.height * scale));
      const c = document.createElement("canvas");
      c.width = w;
      c.height = h;
      const ctx = c.getContext("2d");
      ctx.drawImage(img, 0, 0, w, h);
      const px = ctx.getImageData(0, 0, w, h);
      const d = px.data;
      // Threshold: pixels close to white become transparent so the signature reads clean
      for (let i = 0; i < d.length; i += 4) {
        const r = d[i], g = d[i + 1], b = d[i + 2];
        if (r + g + b > 660) { // near-white background
          d[i + 3] = 0; // full transparency
        } else if (r + g + b > 540) {
          // Soft edges - reduce opacity so anti-aliasing does not create a halo
          d[i + 3] = Math.max(0, d[i + 3] - 140);
        } else {
          // Ink pixels - darken slightly for consistent contrast on Cocoa cream backgrounds
          d[i] = Math.max(0, d[i] - 30);
          d[i + 1] = Math.max(0, d[i + 1] - 30);
          d[i + 2] = Math.max(0, d[i + 2] - 30);
        }
      }
      ctx.putImageData(px, 0, 0);
      resolve(c.toDataURL("image/png"));
    };
    img.onerror = () => reject(new Error("Failed to load image"));
    img.src = dataUrl;
  });

  const handleUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Please pick an image (.png, .jpg, .jpeg)");
    if (file.size > 4 * 1024 * 1024) return toast.error("Signature image must be under 4 MB");
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const cleaned = await runBackgroundRemoval(reader.result);
        setUploaded(cleaned);
      } catch (err) {
        toast.error("Could not process the image. Try a clearer photo.");
      }
    };
    reader.onerror = () => toast.error("Could not read the file");
    reader.readAsDataURL(file);
  };

  const renderTypedToImage = (text) => new Promise((resolve) => {
    const c = document.createElement("canvas");
    c.width = 800; c.height = 200;
    const ctx = c.getContext("2d");
    ctx.clearRect(0, 0, c.width, c.height);
    ctx.font = "italic 62px 'Dancing Script', 'Great Vibes', 'Segoe Script', cursive";
    ctx.fillStyle = "#3E2723";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(text || "", c.width / 2, c.height / 2);
    resolve(c.toDataURL("image/png"));
  });

  const confirm = async () => {
    if (busy || extBusy) return;
    if (!esignConsented) {
      return toast.error("Please acknowledge the ESIGN Act consent below before signing");
    }
    let image = "";
    let method = tab;
    if (tab === "saved") {
      image = savedImage;
    } else if (tab === "draw") {
      if (!drawn) return toast.error("Draw your signature first");
      image = drawn;
    } else if (tab === "type") {
      if (!typed.trim()) return toast.error("Type your name to sign");
      image = await renderTypedToImage(typed.trim());
    } else if (tab === "upload") {
      if (!uploaded) return toast.error("Upload a signature image first");
      image = uploaded;
    }
    setBusy(true);
    try {
      if (saveToProfile && tab !== "saved" && canOfferSaveToProfile) {
        // Persist to the user profile so future prompts default to the saved signature
        await api.put("/me/signature", {
          signature_name: savedName,
          signature_credentials: savedCreds,
          signature_style: user?.signature_style || "cursive",
          signature_image: image,
        });
        if (typeof refresh === "function") await refresh();
      }
      try { localStorage.setItem(esignKey, "1"); } catch { /* storage unavailable */ }
      await onConfirm({ image, name: displayName, method, witness_name: (witness || "").trim() });
    } catch (e) {
      toast.error(apiError(e));
    } finally { setBusy(false); }
  };

  const isBusy = busy || extBusy;

  return (
    <div className="fixed inset-0 z-[90]" role="dialog" aria-modal="true" aria-labelledby="sig-modal-title" data-testid="signature-prompt-modal">
      <div className="absolute inset-0 bg-[#3E2723]/45 backdrop-blur-[2px]" onClick={isBusy ? undefined : onClose} />
      <div className="absolute left-1/2 top-1/2 w-[94vw] max-w-2xl -translate-x-1/2 -translate-y-1/2 rounded-2xl border border-[#EFEBE0] bg-white shadow-2xl">
        <div className="flex items-start justify-between gap-3 border-b border-[#F1EEE7] bg-[#FDFCF8] px-5 py-4 rounded-t-2xl">
          <div>
            <h3 id="sig-modal-title" className="flex items-center gap-2 font-heading text-base font-bold text-[#3E2723]">
              <ShieldCheck size={16} className="text-[#7B6157]" /> {title}
            </h3>
            <p className="mt-0.5 text-[11px] text-[#7B6157]">{subtitle}</p>
          </div>
          <button type="button" data-testid="sig-modal-close" aria-label="Close signature modal" onClick={onClose}
            className="h-8 w-8 shrink-0 rounded-full flex items-center justify-center text-[#7B6157] hover:bg-[#F5F2EB]" disabled={isBusy}>
            <X size={14} />
          </button>
        </div>

        <div className="border-b border-[#F1EEE7] px-5 pt-3">
          <div className="flex gap-1 overflow-x-auto">
            {savedImage && (
              <TabPill active={tab === "saved"} testId="sig-tab-saved" icon={ShieldCheck} label="Saved Signature" onClick={() => setTab("saved")} />
            )}
            <TabPill active={tab === "draw"} testId="sig-tab-draw" icon={PenLine} label="Draw" onClick={() => setTab("draw")} />
            <TabPill active={tab === "type"} testId="sig-tab-type" icon={Type} label="Type" onClick={() => setTab("type")} />
            <TabPill active={tab === "upload"} testId="sig-tab-upload" icon={Upload} label="Upload" onClick={() => setTab("upload")} />
          </div>
        </div>

        <div className="px-5 py-4">
          {reattestNeeded && (
            <div className="mb-3 rounded-lg border border-[#FFB300] bg-[#FFF8E1] px-3 py-2 text-[11px] text-[#8D4E00]" data-testid="sig-reattest-banner">
              Your saved signature is {signatureAgeDays} days old. Annual re-attestation is due (365-day policy). Please draw, type, or upload a fresh signature to keep the audit trail current.
            </div>
          )}
          {tab === "saved" && (
            <div data-testid="sig-saved-panel">
              <div className="rounded-xl border border-[#EFEBE0] bg-[#FDFCF8] p-4 text-center">
                {savedImage ? (
                  <img src={savedImage} alt="Your saved signature" data-testid="sig-saved-preview" className="mx-auto h-20 object-contain" />
                ) : (
                  <div className="text-xs text-[#7B6157]">No saved signature yet. Pick Draw, Type, or Upload.</div>
                )}
                <div className="mt-2 text-xs italic text-[#5D4037]">{displayName}</div>
              </div>
              <div className="mt-2 text-[10px] text-[#7B6157]">
                Loaded from your profile Settings &gt; E-Signature. Tap another tab to sign with a fresh mark.
              </div>
            </div>
          )}
          {tab === "draw" && (
            <div data-testid="sig-draw-panel">
              <SignaturePad value={drawn} onChange={setDrawn} />
              <div className="mt-2 text-[10px] text-[#7B6157]">Draw with a mouse, trackpad, stylus, or your finger on a touchscreen.</div>
            </div>
          )}
          {tab === "type" && (
            <div data-testid="sig-type-panel">
              <label className="block text-[10px] font-bold uppercase text-[#6D4C41] tracking-wide">Type your legal name</label>
              <input type="text" data-testid="sig-type-input" value={typed} onChange={(e) => setTyped(e.target.value)}
                autoFocus placeholder="e.g., Evelyn R. Harper"
                className="mt-1 w-full rounded-lg border border-[#E5DFD3] bg-white px-3 py-2 text-sm outline-none focus:border-[#8D6E63]" />
              <div className="mt-3 rounded-xl border border-[#EFEBE0] bg-[#FDFCF8] p-4 text-center">
                <div data-testid="sig-typed-preview" style={{ fontFamily: "'Dancing Script', 'Great Vibes', 'Segoe Script', cursive" }}
                  className="italic text-3xl text-[#3E2723]">{typed || "Your typed signature"}</div>
              </div>
              <div className="mt-2 text-[10px] text-[#7B6157]">By typing your name and confirming, you agree that this is your legal signature.</div>
            </div>
          )}
          {tab === "upload" && (
            <div data-testid="sig-upload-panel">
              <label className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-[#D7CCC8] bg-[#FDFCF8] p-6 text-center cursor-pointer hover:bg-[#F5F2EB]">
                <Upload size={22} className="text-[#7B6157]" />
                <div className="mt-2 text-sm font-semibold text-[#3E2723]">Upload a signature image</div>
                <div className="mt-0.5 text-[11px] text-[#7B6157]">PNG or JPG up to 4 MB. Background is removed automatically.</div>
                <input type="file" accept="image/png,image/jpeg,image/jpg" onChange={handleUpload}
                  data-testid="sig-upload-input" className="hidden" />
              </label>
              {uploaded && (
                <div className="mt-3">
                  <div className="text-[10px] font-bold uppercase text-[#6D4C41] tracking-wide">Preview after background removal</div>
                  <div className="mt-1 rounded-xl border border-[#EFEBE0] bg-[repeating-linear-gradient(45deg,#F5F2EB_0_6px,#FDFCF8_6px_12px)] p-3">
                    <img src={uploaded} alt="Uploaded signature preview" data-testid="sig-uploaded-preview" className="mx-auto h-24 object-contain" />
                  </div>
                  <div className="mt-2 flex items-center justify-between">
                    <button type="button" data-testid="sig-upload-clear" onClick={() => setUploaded("")}
                      className="flex items-center gap-1 text-xs font-semibold text-[#C62828] hover:underline"><Trash2 size={12} /> Remove</button>
                    <label className="flex items-center gap-1 text-xs font-semibold text-[#7B6157] cursor-pointer hover:underline">
                      <RefreshCcw size={12} /> Replace
                      <input type="file" accept="image/png,image/jpeg,image/jpg" onChange={handleUpload} className="hidden" />
                    </label>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="border-t border-[#F1EEE7] px-5 py-3">
          {withWitness && (
            <div className="mb-3">
              <FLabel>Witness name (optional)</FLabel>
              <FInput data-testid="sig-witness-input" value={witness} onChange={(e) => setWitness(e.target.value)}
                placeholder="Name of the person witnessing this signature" />
            </div>
          )}
          {tab !== "saved" && canOfferSaveToProfile && (
            <label className="mb-2 flex items-center gap-2 text-xs text-[#4E342E]">
              <input type="checkbox" data-testid="sig-save-to-profile" checked={saveToProfile}
                onChange={(e) => setSaveToProfile(e.target.checked)} />
              <span>Save this signature to my profile so future sign actions use it automatically</span>
            </label>
          )}
          <label className="mb-2 flex items-start gap-2 text-xs leading-snug text-[#4E342E]" data-testid="sig-esign-consent-row">
            <input type="checkbox" className="mt-0.5 shrink-0" data-testid="sig-esign-consent"
              checked={esignConsented} onChange={(e) => setEsignConsented(e.target.checked)} />
            <span>
              <strong>ESIGN Act consent:</strong> I agree that my electronic signature has the same legal effect,
              validity, and enforceability as a handwritten signature under the Electronic Signatures in Global and National Commerce Act (15 U.S.C. Chapter 96).
            </span>
          </label>
          <div className="flex items-center justify-between gap-2">
            <div className="text-[10px] leading-tight text-[#7B6157] max-w-xs">
              A signature_id, doc_hash, IP address, and timestamp are recorded for every signed record and appear in the audit log.
            </div>
            <div className="flex gap-2">
              <Btn variant="ghost" onClick={onClose} disabled={isBusy} data-testid="sig-cancel-btn">Cancel</Btn>
              <Btn data-testid="sig-confirm-btn" onClick={confirm} disabled={isBusy}>
                {isBusy ? "Signing…" : "Confirm and Sign"}
              </Btn>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function TabPill({ active, icon: Icon, label, onClick, testId }) {
  return (
    <button type="button" data-testid={testId} onClick={onClick}
      className={`flex items-center gap-1.5 rounded-t-lg px-3 py-2 text-xs font-semibold transition-colors ${active ? "bg-white text-[#3E2723] shadow-sm border-x border-t border-[#EFEBE0] -mb-px" : "text-[#7B6157] hover:bg-[#F5F2EB]"}`}>
      <Icon size={12} /> {label}
    </button>
  );
}
